import React, { Fragment, memo, useMemo, useEffect, useCallback } from "react";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import Icon from "../Icons/Icon";
import HawkeyeV2Selection from "../HawkeyeV2Selection/HawkeyeV2Selection";
import HawkeyeV2FilterSelection from "../HawkeyeV2FilterSelection/HawkeyeV2FilterSelection";
import Toggle from "react-toggle";
import "react-toggle/style.css";

import { getSelectedFilters } from "../HawkeyeV2Filters/HawkeyeV2FilterUtils";

import "./HawkeyeV2CreateFilter.scss";

function HawkeyeV2CreateFilter(props) {
  const {
    widget,
    savedFilters,
    filterOptions,
    smartFilterRefs,
    saveFilter,
    updateSavedFilter,
    closeCreateFilter,
    HawkeyeV2AppSettings = {},
    showPublicToggle,
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      selectedFilter: null,
      filterSaveName: "",
      filterValues: [],
      isFetchingValues: false,
      isOverridingFilter: false,
      selectedFilters: [],
      isPublic: false,
    },
    props.appName,
    props.appID,
    "HawkeyeV2CreateFilter",
    props.componentID
  );

  // Get filter values on selected filter change
  useEffect(() => {
    if (state.selectedFilter) {
      dispatch({
        isFetchingValues: true,
        filterValues: [],
      });
      dispatch(
        {
          filter: state.selectedFilter,
          smartFilterRefs: smartFilterRefs,
          HawkeyeV2AppSettings: HawkeyeV2AppSettings,
        },
        "AxiomEvent",
        "getFilterValues"
      );
    }
  }, [dispatch, state.selectedFilter, smartFilterRefs, HawkeyeV2AppSettings]);

  const onFilterSelection = useCallback(
    (selectedFilter) => {
      const d1 = { ...selectedFilter };
      delete d1["component"];
      dispatch({ selectedFilter: d1 });
    },
    [dispatch]
  );

  const onFilterValueSelection = useCallback(
    (selectedValues) => {
      const filterValues = selectedValues.map((value) => value.displayName);
      const newSelectedFilters = getSelectedFilters(
        state.selectedFilters,
        state.selectedFilter,
        filterValues
      );

      dispatch(
        {
          filters: [
            {
              filter: state.selectedFilter,
              values: filterValues,
            },
          ],
          smartFilterRefs: smartFilterRefs,
        },
        "AxiomEvent",
        "onApplyFilter"
      );

      dispatch({
        selectedFilters: newSelectedFilters.filter((f) => f.values.length),
        selectedFilter: null,
        filterValues: [],
      });
    },
    [dispatch, state.selectedFilter, state.selectedFilters, smartFilterRefs]
  );

  const resetSavedFilters = useCallback(() => {
    if (state.selectedFilters.length > 0) {
      dispatch(
        (state) => ({
          filters: state.selectedFilters,
          smartFilterRefs: smartFilterRefs,
        }),
        "AxiomEvent",
        "onRemoveFilter"
      );
    }
  }, [dispatch, smartFilterRefs, state.selectedFilters.length]);

  const closeCreateWindow = useCallback(() => {
    resetSavedFilters();
    closeCreateFilter();
  }, [closeCreateFilter, resetSavedFilters]);

  const onSaveFilter = () => {
    const { filterSaveName, selectedFilters } = state;
    // Check if a filter with similar name exists in saved filters
    const isAlreadySaved = savedFilters.some((f) => f.filterName === filterSaveName);

    // If user is not overriding any saved filter and current filter is not saved
    // save it in the database
    if (!state.isOverridingFilter && !isAlreadySaved) {
      saveFilter({
        widget: widget,
        name: filterSaveName,
        filters: selectedFilters.map((f) => ({
          filter: {
            displayName: f.filter.displayName,
            key: f.filter.key,
          },
          values: f.values,
        })),
        isPublic: state.isPublic,
      });
      closeCreateWindow();
    }

    // If the filter is already saved and user agreed to override current filter then
    // update it in the database
    if (isAlreadySaved && state.isOverridingFilter) {
      updateSavedFilter({
        widget: widget,
        filterID: savedFilters.find((f) => f.filterName === filterSaveName).filterID,
        filterName: filterSaveName,
        filters: selectedFilters.map((f) => ({
          filter: {
            displayName: f.filter.displayName,
            key: f.filter.key,
          },
          values: f.values,
        })),
      });
      closeCreateWindow();
    }

    // If the current filter is already saved prompt user if he wants to override it
    if (isAlreadySaved && !state.isOverridingFilter) {
      dispatch({ isOverridingFilter: true });
    }
  };

  const updateFilterSaveName = (event) => {
    const filterSaveName = event.target.value;
    dispatch({
      filterSaveName,
      isOverridingFilter: false,
    });
  };

  const computedFilterOptions = useMemo(
    () =>
      filterOptions.map((f) => {
        const filter = { ...f };
        const filterSelectedValues = state.selectedFilters.find((d) => d.filter.key === f.key);
        if (filterSelectedValues && filterSelectedValues.values.length > 0) {
          const selectedValueCount = filterSelectedValues.values.length;
          filter.component = (
            <Fragment>
              {filter.displayName}
              <b>{` (${selectedValueCount} selected)`}</b>
            </Fragment>
          );
        }
        return filter;
      }),
    [filterOptions, state.selectedFilters]
  );

  const selectedFilterValues = useMemo(() => {
    if (state.selectedFilter) {
      const filterSelectedValues = state.selectedFilters.find(
        (d) => d.filter.key === state.selectedFilter.key
      );
      if (filterSelectedValues) {
        return filterSelectedValues.values.map((v) => ({ displayName: v, key: v }));
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [state.selectedFilter, state.selectedFilters]);

  const filterSelectedOptions = useMemo(
    () => (state.selectedFilter ? [state.selectedFilter] : []),
    [state.selectedFilter]
  );

  const togglePublicFilter = useCallback(() => {
    dispatch({
      isPublic: !state.isPublic,
    });
  }, [dispatch, state.isPublic]);

  const icons = useMemo(() => {
    return {
      checked: <div className="unchecked-icon">{""}</div>,
      unchecked: <div>{""}</div>,
    };
  }, []);

  return (
    <div className="hawkeye-filter-modal">
      <div className="hawkeye-filter-modal-content">
        <div className="hawkeye-filter-modal-header">
          <div className="hawkeye-filter-modal-title">{AxiomAPIs.translate("Create a filter")}</div>
          {showPublicToggle != false && (
            <div className="hawkeye-filter-modal-toggle">
              <div
                className="hawkeye-filter-toggle-text"
                title="Public filter is visible to all users who have access to this widget"
              >
                <Icon icon="ICON_INFO" className="hawkeye-filter-tag-icon" />
              </div>
              <div className="hawkeye-filter-toggle-text">Public</div>
              <div className="hawkeye-filter-toggle-btn">
                <Toggle checked={state.isPublic} icons={icons} onChange={togglePublicFilter} />
              </div>
            </div>
          )}
        </div>
        <div className="hawkeye-filter-modal-body">
          <div className="hawkeye-filter-create">
            <div className="hawkeye-filter-save">
              <label className="hawkeye-filter-save-label">
                {AxiomAPIs.translate("Enter a filter name:")}{" "}
              </label>
              <input
                type="text"
                value={state.filterSaveName}
                onChange={updateFilterSaveName}
                placeholder={AxiomAPIs.translate("Enter a name for the filter")}
                className={
                  state.filterSaveName
                    ? "hawkeye-filter-save-input"
                    : "hawkeye-filter-save-input-highlighted"
                }
              />
            </div>
            <div className="hawkeye-filter-create-selection">
              <HawkeyeV2Selection
                className="hawkeye-filter-create-section hawkeye-filter-create-dimensions"
                isSearchable={true}
                options={computedFilterOptions}
                selectedOptions={filterSelectedOptions}
                onSelection={onFilterSelection}
                appName={props.appName}
                appID={props.appID}
                componentID={props.componentID}
              />
              <HawkeyeV2FilterSelection
                isFetchingValues={state.isFetchingValues}
                filterValues={state.filterValues}
                selectedFilter={state.selectedFilter}
                selectedValues={selectedFilterValues}
                onSelection={onFilterValueSelection}
                appName={props.appName}
                appID={props.appID}
                componentID={`selection-value-${props.componentID}`}
              />
            </div>
            {state.isOverridingFilter && (
              <p className="hawkeye-filters-message">
                Filter with name <b>{state.filterSaveName}</b> already exists. Click save again to
                override saved filter &nbsp;
                <b>{state.filterSaveName}</b>. Or enter a new name for your filter.
              </p>
            )}
          </div>
        </div>
        <div className="hawkeye-filter-modal-footer">
          <button onClick={closeCreateWindow} className="hawkeye-btn hawkeye-btn-secondary">
            {AxiomAPIs.translate("Close")}
          </button>
          <button
            disabled={!state.filterSaveName || state.selectedFilters.length === 0}
            onClick={onSaveFilter}
            className="hawkeye-btn hawkeye-btn-primary"
          >
            {AxiomAPIs.translate("Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(HawkeyeV2CreateFilter);
